import { CanvasEvents, FabricObject } from 'fabric';
import { Path } from 'opentype.js';
import { Polygon } from 'polygon-clipping';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import removeMediaElementOperation from 'editor/src/store/design/operation/removeMediaElementOperation';
import { Coords, ElementAddress, MediaText } from 'editor/src/store/design/types';
import { setTextEditModeAction as setTextEditModeOperation } from 'editor/src/store/editor/slice';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import hasPersonalizationPluginEnabled from 'editor/src/store/plugins/selector/hasPersonalizationPluginEnabled';

import CustomFabricImage from 'editor/src/fabric/CustomFabricImage';
import FabricPathText from 'editor/src/fabric/FabricPathText';
import parseSvgPathToCommands from 'editor/src/util/parseSvgPathToCommands';
import useBrowserColor from 'editor/src/util/useBrowserColor';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';
import useMediaElementLiveUpdates from 'editor/src/util/useMediaElementLiveUpdates';

import { currentOperationManager } from 'editor/src/component/EditorArea/ElementOperationOverlay';
import FabricImageComponent from 'editor/src/component/EditorArea/fabricComponents/FabricImageComponent';
import FabricPathComponent from 'editor/src/component/EditorArea/fabricComponents/FabricPathComponent';
import FabricTextInputComponent from 'editor/src/component/EditorArea/fabricComponents/FabricTextInputComponent';
import { setClipPath } from 'editor/src/component/EditorArea/fabricComponents/fabricUtils';
import useSnapMatch from 'editor/src/component/EditorArea/snapping/useSnapMatch';
import ElementLoader from 'editor/src/component/EditorArea/Spread/Page/MediaElement/ElementLoader';
import fabricElementToObjectRect from 'editor/src/component/EditorArea/Spread/Page/MediaElement/fabricElementToObjectRect';
import getElementRect from 'editor/src/component/EditorArea/Spread/Page/MediaElement/getElementRect';
import useHoverBox from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useHoverBox';
import useIsInteractable from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useIsInteractable';
import useMultiSelection from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useMultiSelection';
import useStoreSelection from 'editor/src/component/EditorArea/Spread/Page/MediaElement/useStoreSelection';
import WarningIcon, {
  WarningIconInterface,
} from 'editor/src/component/EditorArea/Spread/Page/MediaElement/WarningIcon';
import zIndex from 'editor/src/component/EditorArea/Spread/zIndex';
import { CanvasRotation } from 'editor/src/component/EditorArea/types';

import fabricPropsToTextElementData from './fabricPropsToTextElementData';
import getDigitizedTextPosition from './getDigitizedTextPosition';
import getSvgOverlayOffset from './getSvgOverlayOffset';
import getTextClipPath from './getTextClipPath';
import getTextShadow from './getTextShadow';
import isEmptyTextEligibleForRemoval from './isEmptyTextEligibleForRemoval';
import textElementDataToFabricProps from './textElementDataToFabricProps';
import { getFlexbileTextMaxWidth } from './updateTextElementWithoutRender';
import useDigitizedText from './useDigitizedText';
import useFontLoader from './useFontLoader';
import useProtectedFontRender from './useProtectedFontRender';
import useTextEditMode from './useTextEditMode';
import useTextUpdates from './useTextUpdates';

// Custom fonts (especially script/handwriting) can have extreme swashes that extend
// far beyond the element bounds. This padding value is passed to getTextClipPath
// so the clip rect is large enough that only the page boundary clips the text.
// Based on max font size 240pt with swashes up to ~4× font size.
// Performance: only affects polygon intersection calculation (two rectangles), negligible cost.
const CUSTOM_FONT_CLIP_PADDING = 1000;

function getClipPadding(fontFamily: string, fontSize: number): number {
  return fontFamily.startsWith('custom-') ? CUSTOM_FONT_CLIP_PADDING : fontSize;
}

interface Props {
  elementData: MediaText;
  elementAddress: ElementAddress;
  pageCoords: Coords;
  areaWidth: number;
  canvasRotation: CanvasRotation;
  contentClipPolygons: Polygon[];
  selected: boolean;
  isMobile: boolean;
  ignorePersonalizationLock: boolean;
  showGuides: boolean;
  contentClipPath: FabricObject | undefined;
}

function Text({
  elementData,
  elementAddress,
  pageCoords,
  areaWidth,
  canvasRotation,
  contentClipPolygons,
  selected,
  isMobile,
  ignorePersonalizationLock,
  showGuides,
  contentClipPath,
}: Props) {
  const { mm2px, px2mm } = useFabricUtils();
  const fabricCanvas = useFabricCanvas();
  const fabricElementRef = useRef<FabricPathText>(null);
  const digitizedImageRef = useRef<CustomFabricImage>(null);
  const warningRef = useRef<WarningIconInterface>(null);
  const dispatch = useDispatch();
  const [digiImageLoaded, setDigiImageLoaded] = useState(false);

  const { liveElement: element, liveUpdate } = useMediaElementLiveUpdates(elementData);

  const personalizationPluginEnabled = useSelector(hasPersonalizationPluginEnabled);

  const isInteractable = useIsInteractable(element, ignorePersonalizationLock);

  const { loadedFontFamily, isFontLoaded } = useFontLoader(element.extra.fontFamily);
  const textEditMode = useTextEditMode(fabricElementRef, selected);
  const { digitizedAsset, showDigitizedAsset, getFabricOptionsOnImageUpdate } = useDigitizedText(
    element,
    textEditMode.enabled,
    px2mm,
  );
  useProtectedFontRender(element, elementAddress, textEditMode.enabled);

  // Overlay that renders element.path (the server-supplied glyph outline) on the canvas.
  // Custom (protected) fonts render the path as a VECTOR (FabricPathComponent) — no
  // rasterization, so no intrinsic-pixel rounding (the right-edge clip) and no per-element
  // bitmap. Standard SSR fonts keep the SVG-image path (element dimensions, original behavior).
  const isCustomFont = element.extra.fontFamily.startsWith('custom-');

  const svgOverlayBbox = useMemo(() => {
    if (!element.path || textEditMode.enabled || !isCustomFont) {
      return undefined;
    }
    const commands = parseSvgPathToCommands(element.path);
    if (commands.length === 0) {
      return undefined;
    }
    const bboxPath = new Path();
    bboxPath.commands = commands;
    return bboxPath.getBoundingBox();
  }, [element.path, textEditMode.enabled, isCustomFont]);

  // Custom (protected) fonts are drawn by the vector overlay below; only standard SSR
  // fonts use the rasterized SVG-image overlay (original behavior).
  const svgPathOverlayUrl = useMemo(() => {
    if (!element.path || textEditMode.enabled || isCustomFont) {
      return undefined;
    }
    const vb = `0 0 ${element.width} ${element.height}`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" width="${element.width}" height="${element.height}"><path d="${element.path}" fill="${element.fill}"/></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }, [element.path, element.fill, element.width, element.height, textEditMode.enabled, isCustomFont]);

  // Scale the SSR-font image so it fits the element dimensions (original behavior).
  const getSvgOverlayFabricOptions = useCallback(
    (image: CustomFabricImage) => {
      const img = image.getElement();
      if (!img.width || !img.height || !element.width || !element.height) {
        return {};
      }
      const scaleX = element.width / px2mm(img.width);
      const scaleY = element.height / px2mm(img.height);
      return { scaleX, scaleY };
    },
    [element.width, element.height, px2mm],
  );

  // Custom-font glyph path is rendered as a vector. The path is in element-local mm, so
  // sx/sy scale the glyph and mm2px(1) converts to canvas pixels (1mm -> mm2px(1)px, the
  // same convention as the diecut/line vector paths). A vector has no intrinsic raster
  // size, so there is no rounding loss and no clipping regardless of sx/sy.
  const showGlyphPathOverlay = !!svgOverlayBbox && !!element.path;
  const glyphOverlayScaleX = (element.sx ?? 1) * mm2px(1);
  const glyphOverlayScaleY = (element.sy ?? 1) * mm2px(1);

  // The base text element is hidden (opacity 0) whenever a substitute is drawn on top:
  //  - embroidery   -> digitized image of the text
  //  - SSR fonts    -> rasterized glyph image (svgPathOverlayUrl)
  //  - custom fonts -> vector glyph path (showGlyphPathOverlay)
  const isReplacedByOverlay =
    (showDigitizedAsset && !!digitizedAsset?.url && digiImageLoaded) || !!svgPathOverlayUrl || showGlyphPathOverlay;

  const textUpdates = useTextUpdates(
    pageCoords,
    element,
    elementAddress,
    fabricElementRef,
    canvasRotation,
    loadedFontFamily,
  );
  useStoreSelection(fabricElementRef, element.uuid, element.type, selected);

  const fabricProps = textElementDataToFabricProps(
    element,
    elementAddress.elementIndex,
    mm2px,
    isInteractable,
    isMobile,
    loadedFontFamily,
  );

  fabricProps.fill = useBrowserColor(fabricProps.fill);
  fabricProps.stroke = useBrowserColor(fabricProps.stroke);

  const elementRect = useMemo(
    () => getElementRect(element, pageCoords, canvasRotation, mm2px),
    [element.x, element.y, element.r, element.width, element.height, pageCoords, canvasRotation, mm2px],
  );

  const svgOverlayOffset = useMemo(
    () =>
      getSvgOverlayOffset({
        bbox: svgOverlayBbox,
        sx: element.sx ?? 1,
        sy: element.sy ?? 1,
        angleDeg: elementRect.angle || 0,
        mm2px,
      }),
    [svgOverlayBbox, element.sx, element.sy, elementRect.angle, mm2px],
  );

  const { selectableFrameRect, activeSelectionOffset } = useMultiSelection(elementData.uuid, elementRect);

  const shadow = useMemo(
    () => getTextShadow(element.extra.shadow, element.extra.fontSize, mm2px),
    [element.extra.shadow, element.extra.fontSize, mm2px],
  );

  const hoverBox = useHoverBox(elementRect, isMobile, selected || !isInteractable, canvasRotation);
  const isMouseDown = useRef(false);

  const customEvents = useMemo(
    () => ({
      'object:enter': () => {
        fabricElementRef.current?.enterEditing();
        fabricElementRef.current?.selectAll();
      },
      'object:escape': () => {
        if (!fabricElementRef.current?.isEditing) {
          // fabric already exits editing on escape
          fabricCanvas.discardActiveObject();
        }
      },
      fontFallbackLoaded: () => {
        fabricCanvas.requestRenderAll();
        textUpdates.onObjectModified();
      },
    }),
    [textUpdates.onObjectModified],
  );

  const onObjectManipulation = useCallback(() => {
    if (textEditMode.enabled) {
      dispatch(setTextEditModeOperation(false));
    }

    if (fabricElementRef.current) {
      if (textEditMode.enabled) {
        setClipPath(fabricElementRef.current, undefined);
      } else {
        const rect = fabricElementToObjectRect(fabricElementRef.current, undefined, false);
        setClipPath(
          fabricElementRef.current,
          getTextClipPath(
            rect,
            getClipPadding(element.extra.fontFamily, fabricElementRef.current.fontSize ?? 0),
            contentClipPolygons,
            contentClipPath,
          ),
        );
      }
    }
  }, [contentClipPolygons, textEditMode.enabled, contentClipPath, element.extra.curve]);

  const onExitEditing = useCallback(() => {
    const text = fabricElementRef.current?.text;

    // Check if element is empty on focus out and if it is delete it.
    // A personalizable text layer (personalization plugin on + toggle switched on,
    // i.e. not personalizationLocked) is exempt: it must survive being emptied so the
    // placeholder box stays as a personalization layer instead of being deleted.
    const isPersonalizableLayer = personalizationPluginEnabled && !element.personalizationLocked;
    const textEligibleForRemove = isEmptyTextEligibleForRemoval(text, isMouseDown.current, isPersonalizableLayer);
    if (textEligibleForRemove) {
      dispatch(removeMediaElementOperation(elementAddress));
    } else if (element.extra.fontFamily?.startsWith('custom-')) {
      // Custom (imported) fonts only: persist the live fabric metrics on exit so
      // the box doesn't revert to the wide imported frame after editing.
      // Standard fonts already persist via the debounced onTextChanged while
      // typing — calling it again here would dispatch an extra media-element
      // operation, corrupting undo/redo step counts and shifting the element.
      textUpdates.onTextChanged();
      textUpdates.flushTextUpdates();
    }

    textEditMode.onExitEditing();
  }, [
    textEditMode.onExitEditing,
    textUpdates,
    elementAddress,
    element.extra.fontFamily,
    element.personalizationLocked,
    personalizationPluginEnabled,
  ]);

  const clipPath = useMemo(
    () =>
      getTextClipPath(
        elementRect,
        getClipPadding(element.extra.fontFamily, element.extra.fontSize),
        contentClipPolygons,
        contentClipPath,
      ),
    [
      element.extra.fontSize,
      element.extra.fontFamily,
      elementRect,
      contentClipPolygons,
      contentClipPath,
      element.extra.curve,
    ],
  );

  const snapMatch = useSnapMatch(element.uuid, showGuides && !element.locked, pageCoords);

  const onMouseDown = useCallback(
    (e: CanvasEvents['mouse:down']) => {
      if (isMobile && element.sample) {
        textEditMode.onEnterEditing();
      }
      isMouseDown.current = true;
      snapMatch.onMouseDown(e);
    },
    [
      snapMatch.onMouseDown,
      isMobile,
      element.sample,
      element.uuid,
      element.name,
      element.extra.text,
      element.width,
      element.height,
      element.extra.fontFamily,
      element.extra.flexibleWidth,
      element.path,
      selected,
      textEditMode.enabled,
    ],
  );

  const onMouseMove = useCallback(
    (e: CanvasEvents['mouse:move']) => {
      if (
        !isMouseDown.current ||
        fabricCanvas.getActiveObject() !== fabricElementRef.current ||
        !fabricElementRef?.current?.oCoords
      ) {
        return;
      }
      snapMatch.onMouseMove(e);
      onObjectManipulation();

      if (fabricElementRef.current) {
        const newElement = {
          ...element,
          ...fabricPropsToTextElementData(px2mm, pageCoords, fabricElementRef.current, canvasRotation),
        };
        liveUpdate(newElement);
        let isElementMove = false;
        if (fabricElementRef.current.oCoords && e.transform) {
          fabricElementRef.current.setCoords();
          isElementMove = e.transform.action === 'drag';
          currentOperationManager.emit(
            'objectUpdating',
            newElement,
            fabricElementRef.current.oCoords,
            isElementMove ? 'move' : 'resize',
          );
        }

        if (digitizedImageRef.current) {
          const { left, top, angle } = fabricElementRef.current;
          const { scaleX, scaleY } = digitizedImageRef.current;
          if (!Number.isNaN(left) && !Number.isNaN(top) && scaleX && scaleY) {
            digitizedImageRef.current.set(
              getDigitizedTextPosition({
                originalLeft: left as number,
                originalTop: top as number,
                scaleX,
                scaleY,
                angle: angle || 0,
                margin: digitizedAsset?.margin || 0,
              }),
            );
          }

          digitizedImageRef.current.set({
            clipPath: fabricElementRef.current.clipPath,
            opacity: digitizedImageRef.current.opacity && isElementMove ? 1 : 0,
          });

          if (fabricElementRef.current.opacity === 0 && !isElementMove) {
            fabricElementRef.current.set('opacity', 1);
          }
        }
      }
    },
    [snapMatch.onMouseMove, px2mm, pageCoords, canvasRotation, onObjectManipulation, digitizedAsset?.margin],
  );

  const onMouseUp = useCallback(() => {
    isMouseDown.current = false;
    snapMatch.onMouseUp();
    currentOperationManager.emit('objectUpdateStop');
  }, [snapMatch.onMouseUp]);

  useEffect(() => {
    // correctly set the clippath after live updates of the element because the height may have changed
    if (fabricElementRef.current) {
      if (textEditMode.enabled) {
        setClipPath(fabricElementRef.current, undefined);
      } else {
        const rect = fabricElementToObjectRect(fabricElementRef.current, undefined, false);
        rect.left -= activeSelectionOffset.left;
        rect.top -= activeSelectionOffset.top;
        setClipPath(
          fabricElementRef.current,
          getTextClipPath(
            rect,
            getClipPadding(element.extra.fontFamily, fabricElementRef.current.fontSize ?? 0),
            contentClipPolygons,
            contentClipPath,
          ),
        );
      }
    }
  }, [elementRect, activeSelectionOffset]);

  const maxWidth = getFlexbileTextMaxWidth(
    elementRect.width,
    elementRect.left,
    element.extra.textAlign,
    pageCoords.left,
    areaWidth,
  );

  useLayoutEffect(() => {
    setDigiImageLoaded(false);
  }, [element.digitizing_hash]);

  const onDigiImageLoaded = useCallback(() => {
    setDigiImageLoaded(true);
  }, []);

  if (!isFontLoaded) {
    return null;
  }

  return (
    <>
      <FabricTextInputComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...fabricProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...selectableFrameRect}
        isFontLoaded={isFontLoaded}
        clipPath={textEditMode.enabled ? undefined : clipPath}
        customEvents={customEvents}
        hoverCursor={selected ? 'move' : 'pointer'}
        borderOpacityWhenMoving={0.4}
        uuid={element.uuid}
        ref={fabricElementRef}
        onMouseDown={textEditMode.enabled ? undefined : onMouseDown}
        onMouseMove={textEditMode.enabled ? undefined : onMouseMove}
        onMouseUp={onMouseUp}
        lockMovementX={textEditMode.enabled || fabricProps.lockMovementX}
        lockMovementY={textEditMode.enabled || fabricProps.lockMovementY}
        onMouseOver={hoverBox.onMouseOver}
        onMouseOut={hoverBox.onMouseOut}
        onModified={textUpdates.onObjectModified}
        onChanged={textUpdates.onTextChanged}
        onEditingEntered={textEditMode.onEnterEditing}
        onEditingExited={onExitEditing}
        opacity={element.hidden || isReplacedByOverlay ? 0 : 1}
        shadow={shadow}
        maxCharLimit={element.extra?.maxCharCount}
        paintFirst="stroke"
        maxWidth={maxWidth}
      />
      {digitizedAsset?.url && !textEditMode.enabled && showDigitizedAsset && (
        <FabricImageComponent
          ref={digitizedImageRef}
          source={digitizedAsset.url}
          left={elementRect.left}
          top={elementRect.top}
          evented={false}
          selectable={false}
          zIndex={fabricProps.zIndex}
          clipPath={textEditMode.enabled ? undefined : clipPath}
          opacity={element.hidden ? 0 : 1}
          uuid={element.uuid}
          getFabricOptionsOnUpdate={getFabricOptionsOnImageUpdate}
          angle={element.r}
          crossOrigin="anonymous"
          onImageLoaded={onDigiImageLoaded}
        />
      )}
      {svgPathOverlayUrl && (
        <FabricImageComponent
          source={svgPathOverlayUrl}
          left={elementRect.left + svgOverlayOffset.x}
          top={elementRect.top + svgOverlayOffset.y}
          evented={false}
          selectable={false}
          zIndex={fabricProps.zIndex}
          clipPath={clipPath}
          opacity={element.hidden ? 0 : 1}
          uuid={element.uuid}
          getFabricOptionsOnUpdate={getSvgOverlayFabricOptions}
          angle={element.r}
          crossOrigin="anonymous"
        />
      )}
      {showGlyphPathOverlay && element.path && (
        <FabricPathComponent
          elementPath={element.path}
          left={elementRect.left + svgOverlayOffset.x}
          top={elementRect.top + svgOverlayOffset.y}
          scaleX={glyphOverlayScaleX}
          scaleY={glyphOverlayScaleY}
          fill={element.fill}
          stroke=""
          evented={false}
          selectable={false}
          zIndex={fabricProps.zIndex}
          clipPath={clipPath}
          opacity={element.hidden ? 0 : 1}
          angle={element.r}
        />
      )}
      {(element.digitizing || (showDigitizedAsset && !digitizedAsset?.url && elementData.stitch_count !== -1)) && (
        <ElementLoader frameRect={elementRect} element={element} />
      )}
      {hoverBox.render()}
      <WarningIcon ref={warningRef} frameRect={elementRect} zIndex={zIndex.WARNING_WATERMARK} element={element} />
    </>
  );
}

export default React.memo(Text);
